import React, { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, Shield, CreditCard, Clock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { API_BASE_URL } from '@/config/api';

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UploadStepProps {
  onFileReady: (file: File, url: string, title: string) => void;
}

const UploadStep: React.FC<UploadStepProps> = ({ onFileReady }) => {
  const { t } = useTranslation('quicksend');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [converting, setConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback(async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast({
        title: 'Error',
        description: t('error_file_too_large', { size: sizeMB, max: MAX_FILE_SIZE_MB }),
        variant: 'destructive',
      });
      return;
    }

    const name = file.name.replace(/\.[^/.]+$/, '');
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    if (!ext || !allowedExtensions.includes(ext)) {
      toast({
        title: t('error_unsupported_title', 'Unsupported file type'),
        description: t('error_unsupported_type', 'Only PDF, DOC, and DOCX files are supported. Please select a valid document.'),
        variant: 'destructive',
      });
      return;
    }

    if (ext === 'doc' || ext === 'docx') {
      setConverting(true);
      try {
        const formData = new FormData();
        formData.append('document', file);
        const response = await fetch(`${API_BASE_URL}/guest/documents/convert`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || 'Conversion failed');
        }
        const blob = await response.blob();
        const pdfFile = new File([blob], name + '.pdf', { type: 'application/pdf' });
        onFileReady(pdfFile, URL.createObjectURL(blob), name);
      } catch {
        toast({ title: 'Error', description: t('error_conversion_failed'), variant: 'destructive' });
      } finally {
        setConverting(false);
      }
    } else {
      onFileReady(file, URL.createObjectURL(file), name);
    }
  }, [t, toast, onFileReady]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  return (
    <div className="flex flex-col items-center justify-center flex-1 min-h-[60vh]">
      {/* Hero heading */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-green/10 border border-green/20 text-green-dark px-4 py-1.5 rounded-full text-sm font-medium mb-6">
          <span className="w-1.5 h-1.5 bg-green rounded-full animate-pulse" />
          {t('badge_no_account', 'No account needed')}
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-navy tracking-tight mb-3 leading-tight">
          {t('page_title')}
        </h1>
        <p className="text-gray-500 max-w-lg mx-auto text-base">
          {t('page_subtitle')}
        </p>
      </div>

      {/* Upload dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleFileDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          w-full max-w-xl p-10 sm:p-14 rounded-2xl text-center cursor-pointer transition-all duration-200
          border-2 border-dashed
          ${isDragging
            ? 'border-green bg-green/5 shadow-lg shadow-green/10 scale-[1.01]'
            : 'border-gray-300 bg-white hover:border-green/50 hover:shadow-lg hover:shadow-green/5'
          }
        `}
      >
        {converting ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green/10 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-green" />
            </div>
            <p className="text-sm text-gray-500 font-medium">{t('upload_converting')}</p>
          </div>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-5">
              <Upload className="h-7 w-7 text-navy/60" />
            </div>
            <p className="text-lg font-semibold text-navy mb-1">{t('upload_title')}</p>
            <p className="text-sm text-gray-500 mb-1">{t('upload_description')}</p>
            <p className="text-xs text-gray-400 mb-5">{t('upload_formats')}</p>
            <button
              type="button"
              className="inline-flex items-center gap-2 bg-green text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-green-dark transition-all hover:-translate-y-px shadow-sm hover:shadow-md text-sm"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            >
              <Upload className="h-4 w-4" />
              {t('upload_browse', 'Browse Files')}
            </button>
            <p className="text-xs text-gray-400 mt-4">
              {t('upload_max_size', { size: MAX_FILE_SIZE_MB })}
            </p>
          </>
        )}
      </div>

      {/* Trust indicators */}
      <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 text-green" />
          {t('trust_esign', 'ESIGN & UETA compliant')}
        </span>
        <span className="flex items-center gap-1.5">
          <CreditCard className="h-3.5 w-3.5 text-green" />
          {t('trust_no_card', 'No credit card required')}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-green" />
          {t('trust_ready', 'Ready in under 60 seconds')}
        </span>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={handleFileInput}
      />
    </div>
  );
};

export default UploadStep;
